import { ApolloClient } from '@apollo/client'
import { combineLatest, filter, from, Observable, tap } from 'rxjs'
import { catchError, map, switchMap, take } from 'rxjs/operators'

import {
  CartFragment,
  Maybe,
  SetBillingAddressAndPaymentMethodOnCartDocument,
  SetBillingAddressAndPaymentMethodOnCartMutation,
  SetBillingAddressAndPaymentMethodOnCartMutationVariables,
  SetBillingAddressOnCartDocument,
  SetBillingAddressOnCartMutation,
  SetBillingAddressOnCartMutationVariables,
} from '../../../graphql/magento'
import { isNotNull } from '../../../utils/collectionTools'
import { coerceCaughtToLeft } from '../../../utils/rx/errors'
import { latestAccessToken$ } from '../../auth/state'
import { latestApolloClient$ } from '../../graphql/apollo'
import { cartAddressInputFromFieldValues, shouldSaveInAddressBook } from '../address'
import { shouldUpdateBillingAddress } from '../billing'
import { CustomerAddressFromQuery, ValidFormValues } from '../types'

export interface UpdateBillingAddressIfNeededInput {
  // form values submitted by user
  addressValues: ValidFormValues.Address
  // existing customer addresses - used to determine if address is new and should be saved in the address book
  customerAddresses?: Maybe<CustomerAddressFromQuery[]>
  // used to determine if billing address should be updated (and extract cart id for mutation)
  customerCart: CartFragment
}

const billingAddressUpdateFactory = (
  client: ApolloClient<unknown>,
  token: string,
  variables: SetBillingAddressOnCartMutationVariables,
) =>
  from(
    client.mutate({
      mutation: SetBillingAddressOnCartDocument,
      variables,
      context: { token },
      errorPolicy: 'none',
    }),
  ).pipe(
    // ignore `errors` in FetchResult since `errorPolicy` is `none` (Apollo will throw if errors returned from server
    map(({ data }) => ({ _tag: 'Right' as const, data })),
    tap(() => {
      if (variables.billingAddress.address?.save_in_address_book) {
        // evict customer addresses from cache after saving new address
        client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'customer' })
        client.cache.gc()
      }
    }),
    catchError(coerceCaughtToLeft),
  )

const billingAddressAndPaymentMethodUpdateFactory = (
  client: ApolloClient<unknown>,
  token: string,
  variables: SetBillingAddressAndPaymentMethodOnCartMutationVariables,
) =>
  from(
    client.mutate({
      mutation: SetBillingAddressAndPaymentMethodOnCartDocument,
      variables,
      context: { token },
      errorPolicy: 'none',
    }),
  ).pipe(
    map(({ data }) => ({ _tag: 'Right' as const, data })),
    tap(() => {
      if (variables.billingAddress.address?.save_in_address_book) {
        // evict customer addresses from cache after saving new address
        client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'customer' })
        client.cache.gc()
      }
    }),
    catchError(coerceCaughtToLeft),
  )

export const billingAddressUpdateParamsIfNeeded = ({
  addressValues,
  customerAddresses,
  customerCart,
}: {
  addressValues: ValidFormValues.Address
  customerAddresses?: Maybe<CustomerAddressFromQuery[]>
  customerCart: CartFragment
}): SetBillingAddressOnCartMutationVariables['billingAddress'] | undefined =>
  shouldUpdateBillingAddress({ cart: customerCart, addressValues })
    ? {
        address: {
          ...cartAddressInputFromFieldValues(addressValues),
          save_in_address_book: shouldSaveInAddressBook({
            customerAddresses,
            fieldValues: addressValues,
          }),
        },
        // since billing address is updated after shipping, always set same_as_shipping to false
        same_as_shipping: false,
      }
    : undefined

export const fromUpdateBillingAddress = (
  variables: SetBillingAddressOnCartMutationVariables,
): Observable<
  | { _tag: 'Left'; error: Error }
  | { _tag: 'Right'; data: SetBillingAddressOnCartMutation | null | undefined }
> =>
  combineLatest([latestApolloClient$, latestAccessToken$.pipe(filter(isNotNull))]).pipe(
    take(1),
    switchMap(([client, token]) => billingAddressUpdateFactory(client, token, variables)),
  )

export const fromUpdateBillingAddressAndPaymentMethod = (
  variables: SetBillingAddressAndPaymentMethodOnCartMutationVariables,
): Observable<
  | { _tag: 'Left'; error: Error }
  | { _tag: 'Right'; data: SetBillingAddressAndPaymentMethodOnCartMutation | null | undefined }
> =>
  combineLatest([latestApolloClient$, latestAccessToken$.pipe(filter(isNotNull))]).pipe(
    take(1),
    switchMap(([client, token]) =>
      billingAddressAndPaymentMethodUpdateFactory(client, token, variables),
    ),
  )
